import React, { useEffect, useMemo, useState } from 'react';
import {
  bookingHelperTextStyle,
  bookingPricingModalTitleStyle,
  bookingSettingsCardTitleStyle,
} from '../../styles/booking-typography';
import {
  bookingBrandFontFamilyVar,
  bookingBrandInfoChromeStyles,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import { ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, useIsMobile } from '../ui';
import { formatMoney } from '../../utils/format-money';

export interface MultiPlayerPricingOption {
  customerTypeId: string;
  customerTypeName: string;
  price: number;
  color: string;
}

interface TeeTimeSummary {
  courseName: string;
  dateTime: Date;
  originalDate?: string;
}

export interface MultiPlayerRatePick {
  customerTypeId: string;
  price: number;
}

export interface MultiPlayerPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  teeTime: TeeTimeSummary | null;
  playerCount: number;
  pricingOptions: MultiPlayerPricingOption[];
  /** Player 2+ slots with a linked member — green fee follows member pricing (not editable here). */
  linkedPlayerSlots?: number[];
  /** Optional "First Last" for linked rows */
  linkedSlotLabels?: Record<number, string>;
  isMemberBooker: boolean;
  defaultGuestCustomerTypeId?: string;
  /** Logged-in member’s CRM type — matched to a pricing option when possible */
  bookerCustomerTypeId?: string;
  /** When reopening from checkout — prefills editable rows */
  initialCustomerTypeByPlayer?: Record<number, string>;
  onConfirm: (selections: Record<number, MultiPlayerRatePick>) => void;
  isAuthenticated: boolean;
  brandColors?: {
    fontFamily: string;
    textColor: string;
    secondaryColor: string;
    /** Primary / accent — used for very light info-card tint */
    primaryColor?: string;
    buttonColor: string;
    buttonTextColor: string;
  };
  bookerSlotLabel?: string;
  /** Increment when opening the modal so selections reset from props (avoids stale state when reopening). */
  initNonce?: number;
}

const focusRingClass =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--tebox-focus-ring)]';

function pickMemberDefaultOption(
  opts: MultiPlayerPricingOption[],
  bookerCustomerTypeId?: string
): MultiPlayerPricingOption {
  if (bookerCustomerTypeId) {
    const m = opts.find((o) => o.customerTypeId === bookerCustomerTypeId);
    if (m) return m;
  }
  return opts[0];
}

/** Course default guest type when it appears in options; otherwise lowest-priced guest rate. */
function pickGuestDefaultOption(
  opts: MultiPlayerPricingOption[],
  configuredGuestTypeId?: string
): MultiPlayerPricingOption {
  if (configuredGuestTypeId) {
    const m = opts.find((o) => o.customerTypeId === configuredGuestTypeId);
    if (m) return m;
  }
  const sorted = [...opts].sort((a, b) => a.price - b.price);
  return sorted[0] || opts[0];
}

export const MultiPlayerPricingModal: React.FC<MultiPlayerPricingModalProps> = ({
  isOpen,
  onClose,
  teeTime,
  playerCount,
  pricingOptions,
  linkedPlayerSlots = [],
  linkedSlotLabels = {},
  isMemberBooker,
  defaultGuestCustomerTypeId,
  bookerCustomerTypeId,
  initialCustomerTypeByPlayer,
  onConfirm,
  isAuthenticated,
  brandColors: brandColorsProp,
  bookerSlotLabel,
  initNonce = 0,
}) => {
  const isMobile = useIsMobile();
  const tebox = useBookingTebox();
  const ds = teboxDesignSystem;
  const brandColors = brandColorsProp ?? {
    fontFamily: 'Inter',
    textColor: tebox.colors.textPrimary,
    secondaryColor: tebox.colors.textSecondary,
    primaryColor: undefined,
    buttonColor: '#000000',
    buttonTextColor: '#FFFFFF',
  };

  /** Very light wash of club primary / button accent for info surfaces (Radix modal). */
  const brandInfoCardChrome = useMemo(
    () =>
      bookingBrandInfoChromeStyles({
        primaryColor: brandColors.primaryColor,
        buttonColor: brandColors.buttonColor,
      }),
    [brandColors.primaryColor, brandColors.buttonColor],
  );

  /** Matches checkout applied-pass copy — darkened brand primary. */
  const pricingModalBrandAccentInk = useMemo(
    () =>
      `color-mix(in srgb, ${(brandColors.primaryColor?.trim() || brandColors.buttonColor?.trim() || '#000000')} 46%, black)`,
    [brandColors.primaryColor, brandColors.buttonColor],
  );

  const linkedSet = useMemo(
    () => new Set(linkedPlayerSlots),
    [linkedPlayerSlots.join(',')]
  );
  const sortedOptions = useMemo(
    () => [...pricingOptions].sort((a, b) => a.price - b.price),
    [pricingOptions]
  );

  const [selectedTypeByPlayer, setSelectedTypeByPlayer] = useState<Record<number, string>>({});
  /** Which player row has the rate list open (Passes-style dropdown). */
  const [rateDropdownOpenPn, setRateDropdownOpenPn] = useState<number | null>(null);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isContinueHovered, setIsContinueHovered] = useState(false);

  useEffect(() => {
    if (!isOpen || !pricingOptions.length || playerCount < 1) return;

    const next: Record<number, string> = {};
    for (let pn = 1; pn <= playerCount; pn++) {
      if (pn > 1 && linkedSet.has(pn)) continue;

      const seed = initialCustomerTypeByPlayer?.[pn];
      if (seed && pricingOptions.some((o) => o.customerTypeId === seed)) {
        next[pn] = seed;
        continue;
      }

      if (isMemberBooker) {
        const d = pickMemberDefaultOption(pricingOptions, bookerCustomerTypeId);
        next[pn] = d.customerTypeId;
      } else {
        next[pn] = pickGuestDefaultOption(pricingOptions, defaultGuestCustomerTypeId).customerTypeId;
      }
    }
    setSelectedTypeByPlayer(next);
  }, [
    isOpen,
    initNonce,
    playerCount,
    pricingOptions,
    isMemberBooker,
    bookerCustomerTypeId,
    defaultGuestCustomerTypeId,
    initialCustomerTypeByPlayer,
    linkedPlayerSlots.join(','),
  ]);

  useEffect(() => {
    if (!isOpen) setRateDropdownOpenPn(null);
  }, [isOpen]);

  useEffect(() => {
    setRateDropdownOpenPn(null);
  }, [initNonce, playerCount]);

  if (!teeTime || !pricingOptions.length) return null;

  /** Modal panel is always `bg-white`; brand `textColor` / `secondaryColor` are often light (page is dark) — use TEBOX ink here. */
  const modalInkPrimary = tebox.colors.textPrimary;
  const modalInkSecondary = tebox.colors.textSecondary;

  const titleStyle = bookingPricingModalTitleStyle(brandColors.fontFamily, {
    textAlign: 'left',
    margin: 0,
    color: modalInkPrimary,
  });
  const playerRowHeadingStyle = bookingSettingsCardTitleStyle(brandColors.fontFamily, {
    textAlign: 'left',
    margin: 0,
    marginBottom: ds.spacing.sm,
    color: modalInkPrimary,
  });
  const modalSubtextStyle = bookingHelperTextStyle(brandColors.fontFamily, {
    textAlign: 'left',
    margin: 0,
  });

  const formatTime = (dateTime: Date) => {
    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (originalDate?: string) => {
    const dateString = originalDate || teeTime.dateTime.toISOString().split('T')[0];
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleConfirm = () => {
    const out: Record<number, MultiPlayerRatePick> = {};
    for (let pn = 1; pn <= playerCount; pn++) {
      if (pn > 1 && linkedSet.has(pn)) continue;
      const id = selectedTypeByPlayer[pn];
      const opt = pricingOptions.find((o) => o.customerTypeId === id);
      if (opt) {
        out[pn] = { customerTypeId: opt.customerTypeId, price: opt.price };
      }
    }
    if (!out[1]) return;
    onConfirm(out);
  };

  const playerTitle = (pn: number) => {
    if (pn === 1) {
      return bookerSlotLabel ? `${bookerSlotLabel} (Player 1)` : 'You (Player 1)';
    }
    if (linkedSet.has(pn) && linkedSlotLabels[pn]) {
      return `${linkedSlotLabels[pn]} (Player ${pn})`;
    }
    return `Player ${pn}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseButton
        data-rate-picker-modal=""
        aria-describedby="multi-pricing-modal-description"
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-[560px]"
        style={{
          borderRadius: 'var(--brand-border-radius, 6px)',
          border: `1px solid ${ds.colors.border.light}`,
          boxShadow: ds.shadows.xl,
        }}
      >
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            padding: '24px',
            paddingBottom: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: ds.spacing.lg,
          }}
        >
          <DialogHeader
            className="space-y-0 text-left"
            style={{ margin: 0, padding: 0, gap: ds.spacing.sm }}
          >
            <DialogTitle style={titleStyle}>Rate Selector</DialogTitle>
            <div
              id="multi-pricing-modal-description"
              style={{
                margin: 0,
                padding: 0,
                fontFamily: bookingBrandFontFamilyVar,
                textAlign: 'left',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: ds.typography.body.fontSize,
                  fontWeight: 500,
                  lineHeight: 1.25,
                  color: modalInkPrimary,
                }}
              >
                {teeTime.courseName}
              </p>
              <p
                style={{
                  margin: `${ds.spacing.xs} 0 0`,
                  fontSize: ds.typography.bodySmall.fontSize,
                  lineHeight: ds.typography.bodySmall.lineHeight,
                  fontWeight: 400,
                  color: modalInkSecondary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatTime(teeTime.dateTime)}
                <span style={{ color: ds.colors.text.tertiary, userSelect: 'none' }}>
                  {' '}
                  ·{' '}
                </span>
                {formatDate(teeTime.originalDate)}
              </p>
            </div>
          </DialogHeader>

          {isMemberBooker ? (
            <div
              role="note"
              style={{
                padding: '16px',
                borderRadius: 'var(--brand-border-radius, 6px)',
                ...brandInfoCardChrome,
              }}
            >
              <p
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: isMobile
                    ? ds.typography.bodySmall.fontSize
                    : ds.typography.body.fontSize,
                  lineHeight: 1.5,
                  color: pricingModalBrandAccentInk,
                  margin: 0,
                  textAlign: 'left',
                }}
              >
                Member rates apply to your round by default. Your own green fee is your member rate;
                choose rates for other players below.
              </p>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: ds.spacing.md }}>
            {Array.from({ length: playerCount }, (_, i) => i + 1).map((pn) => {
              const isLinked = pn > 1 && linkedSet.has(pn);
              const linkedOpt =
                isLinked && initialCustomerTypeByPlayer?.[pn]
                  ? pricingOptions.find((o) => o.customerTypeId === initialCustomerTypeByPlayer[pn])
                  : null;
              const displayPrice = linkedOpt ? linkedOpt.price : null;
              const displayName = linkedOpt?.customerTypeName;
              const isBookerMemberReadOnly = isMemberBooker && pn === 1;

              return (
                <div
                  key={`player-rate-${pn}`}
                  style={{
                    paddingBottom: ds.spacing.md,
                    borderBottom:
                      pn === playerCount ? 'none' : `1px solid ${ds.colors.border.light}`,
                  }}
                >
                  <h3 style={playerRowHeadingStyle}>{playerTitle(pn)}</h3>

                  {isLinked ? (
                    <div
                      role="note"
                      style={{
                        padding: '12px 14px',
                        borderRadius: 'var(--brand-border-radius, 6px)',
                        ...brandInfoCardChrome,
                        fontFamily: bookingBrandFontFamilyVar,
                        fontSize: ds.typography.bodySmall.fontSize,
                        color: modalInkPrimary,
                        lineHeight: 1.45,
                      }}
                    >
                      <strong style={{ fontWeight: 500 }}>Linked member</strong>
                      <span style={{ color: tebox.colors.textSecondary }}>
                        {' '}
                        — green fee uses member pricing for this round
                        {displayName != null && displayPrice != null ? (
                          <>
                            {' '}
                            ({displayName} · ${formatMoney(displayPrice)})
                          </>
                        ) : null}
                        . Remove this partner on checkout to pick a different rate.
                      </span>
                    </div>
                  ) : isBookerMemberReadOnly ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: ds.spacing.xs,
                      }}
                    >
                      <span
                        id={`player-rate-label-${pn}-readonly`}
                        style={{
                          ...bookingHelperTextStyle(brandColors.fontFamily, {
                            margin: 0,
                            fontSize: ds.typography.caption.fontSize,
                            fontWeight: 500,
                            color: tebox.colors.textTertiary,
                          }),
                        }}
                      >
                        Green fee rate
                      </span>
                      <div
                        role="status"
                        aria-labelledby={`player-rate-label-${pn}-readonly`}
                        aria-label="Your member green fee rate (not editable)"
                        className={`w-full ${isMobile ? 'px-4 py-3.5' : 'px-4 py-3'} border border-tebox-border rounded-[6px] bg-white`}
                        style={{
                          boxSizing: 'border-box',
                          minHeight: isMobile ? '44px' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          fontFamily: bookingBrandFontFamilyVar,
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: isMobile ? '15px' : '14px',
                          lineHeight: 1.35,
                          color: brandColors.textColor,
                          fontVariantNumeric: 'tabular-nums',
                          cursor: 'default',
                        }}
                      >
                        {(() => {
                          const tid = selectedTypeByPlayer[pn];
                          const opt = sortedOptions.find(
                            (o) => o.customerTypeId === tid
                          );
                          return opt != null
                            ? `${opt.customerTypeName} · $${formatMoney(opt.price)}`
                            : 'Member rate';
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: ds.spacing.xs,
                      }}
                    >
                      <label
                        id={`player-rate-label-${pn}`}
                        htmlFor={`player-rate-trigger-${pn}`}
                        style={{
                          ...bookingHelperTextStyle(brandColors.fontFamily, {
                            margin: 0,
                            fontSize: ds.typography.caption.fontSize,
                            fontWeight: 500,
                            color: tebox.colors.textTertiary,
                          }),
                        }}
                      >
                        Green fee rate
                      </label>
                      <div className="relative min-w-0 w-full">
                        <button
                          type="button"
                          id={`player-rate-trigger-${pn}`}
                          className={`${focusRingClass} w-full flex items-center justify-between gap-2 ${isMobile ? 'px-4 py-3.5 text-tebox-body' : 'px-4 py-3 text-tebox-body'} rounded-[6px] text-left focus:outline-none`}
                          aria-haspopup="listbox"
                          aria-expanded={rateDropdownOpenPn === pn}
                          aria-controls={`player-rate-listbox-${pn}`}
                          aria-label={`Green fee rate for ${playerTitle(pn)}`}
                          onClick={() => {
                            setRateDropdownOpenPn((cur) => (cur === pn ? null : pn));
                          }}
                          style={{
                            ...brandInfoCardChrome,
                            boxSizing: 'border-box',
                            fontFamily: bookingBrandFontFamilyVar,
                            fontStyle: 'normal',
                            fontWeight: 400,
                            fontSize: isMobile ? '15px' : '14px',
                            color: brandColors.textColor,
                            minHeight: isMobile ? '44px' : 'auto',
                          }}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {rateDropdownOpenPn === pn
                              ? 'Hide rates'
                              : (() => {
                                  const tid = selectedTypeByPlayer[pn];
                                  const opt = sortedOptions.find((o) => o.customerTypeId === tid);
                                  return opt
                                    ? `${opt.customerTypeName} · $${formatMoney(opt.price)}`
                                    : 'Select green fee rate';
                                })()}
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${
                              rateDropdownOpenPn === pn ? 'rotate-180' : ''
                            }`}
                            style={{ color: brandColors.secondaryColor }}
                            aria-hidden
                            strokeWidth={2}
                          />
                        </button>
                        {rateDropdownOpenPn === pn ? (
                          <div
                            id={`player-rate-listbox-${pn}`}
                            role="listbox"
                            aria-labelledby={`player-rate-label-${pn}`}
                            className={`mt-1 max-h-48 overflow-y-auto divide-y divide-tebox-border rounded-[6px] border border-tebox-border bg-white shadow-lg`}
                          >
                            {sortedOptions.map((option) => (
                              <button
                                key={option.customerTypeId}
                                type="button"
                                role="option"
                                aria-selected={selectedTypeByPlayer[pn] === option.customerTypeId}
                                className={`w-full ${isMobile ? 'px-4 py-3.5' : 'px-4 py-3'} text-left hover:bg-tebox-hover`}
                                style={{
                                  fontFamily: bookingBrandFontFamilyVar,
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  fontSize: isMobile ? '15px' : '14px',
                                  minHeight: isMobile ? '44px' : 'auto',
                                }}
                                onClick={() => {
                                  setSelectedTypeByPlayer((prev) => ({
                                    ...prev,
                                    [pn]: option.customerTypeId,
                                  }));
                                  setRateDropdownOpenPn(null);
                                }}
                              >
                                <div
                                  className="font-medium text-tebox-ink"
                                  style={{ fontSize: isMobile ? '15px' : '14px' }}
                                >
                                  {option.customerTypeName}
                                </div>
                                <div
                                  className={`${isMobile ? 'text-tebox-body' : 'text-tebox-body'} text-tebox-muted`}
                                  style={{ fontSize: isMobile ? '13px' : '14px' }}
                                >
                                  ${formatMoney(option.price)}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!isAuthenticated ? (
            <div
              role="note"
              style={{
                padding: '16px',
                borderRadius: 'var(--brand-border-radius, 6px)',
                ...brandInfoCardChrome,
              }}
            >
              <p style={{ ...modalSubtextStyle, lineHeight: 1.5, color: modalInkSecondary }}>
                <strong style={{ color: modalInkPrimary, fontWeight: 500 }}>Note:</strong>{' '}
                You&apos;ll be asked to create an account or log in after this step to complete the
                booking.
              </p>
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: ds.spacing.sm,
              paddingTop: ds.spacing.xs,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              onMouseEnter={() => setIsCloseHovered(true)}
              onMouseLeave={() => setIsCloseHovered(false)}
              className={focusRingClass}
              style={{
                padding: '12px 24px',
                fontSize: ds.typography.body.fontSize,
                fontWeight: 500,
                color: modalInkPrimary,
                backgroundColor: isCloseHovered ? tebox.colors.border : tebox.colors.bgSecondary,
                border: `1px solid ${ds.colors.border.light}`,
                borderRadius: 'var(--brand-border-radius, 6px)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: bookingBrandFontFamilyVar,
                minWidth: '120px',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              onMouseEnter={() => setIsContinueHovered(true)}
              onMouseLeave={() => setIsContinueHovered(false)}
              className={focusRingClass}
              style={{
                padding: '12px 24px',
                fontSize: ds.typography.body.fontSize,
                fontWeight: 500,
                color: brandColors.buttonTextColor,
                backgroundColor: isContinueHovered ? brandColors.buttonColor : brandColors.buttonColor,
                opacity: isContinueHovered ? 0.92 : 1,
                border: 'none',
                borderRadius: 'var(--brand-border-radius, 6px)',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                fontFamily: bookingBrandFontFamilyVar,
                minWidth: '160px',
              }}
            >
              Continue to checkout
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
